import { useState, useCallback, useRef, useEffect } from 'react';

export const useCallState = () => {
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // idle, ringing, active, held
  const [callDirection, setCallDirection] = useState(null); // 'inbound' | 'outbound' | null
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callConnection, setCallConnection] = useState(null);
  const durationInterval = useRef(null);

  // Auto-increment call duration while active
  useEffect(() => {
    if (callStatus === 'active') {
      setCallDuration(0);
      durationInterval.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
        durationInterval.current = null;
      }
    }

    return () => {
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
      }
    };
  }, [callStatus]);

  const startOutgoingCall = useCallback((connection) => {
    setActiveCall(connection);
    setCallDirection('outbound');
    setCallStatus('active');
    setIsMuted(false);
  }, []);

  // An inbound leg that has already been accepted — it goes straight to 'active' because the
  // dashboard auto-answers queue calls rather than presenting a ringing state.
  const startIncomingCall = useCallback((connection) => {
    setActiveCall(connection);
    setIncomingCall(null);
    setCallDirection('inbound');
    setCallStatus('active');
    setIsMuted(false);
  }, []);

  const receiveIncomingCall = useCallback((connection) => {
    setIncomingCall(connection);
    setCallDirection('inbound');
    setCallStatus('ringing');
  }, []);

  const endCall = useCallback(() => {
    setActiveCall(null);
    setIncomingCall(null);
    setCallDirection(null);
    setCallStatus('idle');
    setIsMuted(false);
    setCallConnection(null);
    setCallDuration(0);
  }, []);

  const holdCall = useCallback(() => {
    setCallStatus('held');
  }, []);

  const resumeCall = useCallback(() => {
    setCallStatus('active');
  }, []);

  return {
    activeCall,
    incomingCall,
    callStatus,
    callDirection,
    isMuted,
    setIsMuted,
    callDuration,
    callConnection,
    setCallConnection,
    startOutgoingCall,
    startIncomingCall,
    receiveIncomingCall,
    endCall,
    holdCall,
    resumeCall,
  };
};
